import { UploadPhase } from '../../../services/upload/uploadPhase';

/**
 * Phase → user-facing copy. Strings lifted from the creator-app demo so the
 * product narrative stays consistent across stages.
 *
 * `banner` is shown in the top-of-viewport banner; absent when the phase
 * doesn't warrant a banner (e.g. plain `Uploading` is conveyed by the ring
 * alone). `title` and `sub` drive the header area above the progress ring.
 */
export interface UploadPhaseCopy {
  title: string;
  sub: string;
  banner?: {
    text: string;
    /** When true, banner renders in the "informational" (light grey) style;
     *  when false, in the "alert" (brand-tinted) style. */
    informational: boolean;
  };
}

export const UPLOAD_COPY: Record<UploadPhase, UploadPhaseCopy> = {
  [UploadPhase.Idle]: {
    title: '',
    sub: '',
  },
  [UploadPhase.CheckingConnection]: {
    title: 'Checking connection…',
    sub: 'Analyzing upload speed to optimize chunk size.',
  },
  [UploadPhase.Uploading]: {
    title: 'Uploading your video',
    sub: 'Adaptive chunking is active.',
  },
  [UploadPhase.UploadingSlow]: {
    title: 'Uploading on a slower connection',
    sub: 'We are shrinking chunks to keep progress stable.',
  },
  [UploadPhase.Dipped]: {
    title: 'Connection dipped — retrying part',
    sub: 'Minor interruption detected.',
    banner: {
      text: "Upload interrupted — we'll automatically retry when your connection stabilizes.",
      informational: true,
    },
  },
  [UploadPhase.PausedWifi]: {
    title: 'Upload paused for Wi-Fi',
    sub: 'Saved on this device for later (when supported).',
    banner: {
      text: 'Upload paused. Resume when you are on Wi-Fi.',
      informational: true,
    },
  },
  [UploadPhase.Resuming]: {
    // Copy intentionally mirrors the initial-upload "Preparing…" state so
    // the user sees a familiar setup message during both cross-session
    // resume (post-file-repick) and in-session Wi-Fi resume — until the
    // first chunk actually moves, at which point the phase flips back to
    // Uploading.
    title: 'Preparing your video for upload',
    sub: 'Continuing from where you left off.',
  },
  [UploadPhase.Finalizing]: {
    title: 'Finalizing upload…',
    sub: 'Verifying and saving your video securely.',
  },
  [UploadPhase.NeedsRepick]: {
    title: 'Pick the same video to continue',
    sub: 'Your earlier upload is saved and ready to resume.',
  },
  [UploadPhase.Complete]: {
    title: '',
    sub: '',
  },
  [UploadPhase.ErrorFatal]: {
    title: 'Upload failed',
    sub: "We couldn't complete this upload. Try selecting your video again.",
    banner: {
      text: 'Upload failed. Please try again.',
      informational: false,
    },
  },
};
